import Phaser from 'phaser';

export interface DialogLine {
  speaker: string;
  text: string;
  portrait?: string; // Optional atlas frame for speaker portrait
}

export interface DialogChoice {
  text: string;
  next?: string; // ID of next dialog to jump to
  action?: () => void; // Callback for custom logic
}

export interface DialogConfig {
  lines: DialogLine[];
  choices?: DialogChoice[];
  onComplete?: () => void;
}

/**
 * DialogManager - RPG-style dialog system using rexrainbow TextTyping
 * 
 * Features:
 * - Typewriter text effect
 * - Speaker name display
 * - Portrait support (optional)
 * - Choice selection
 * - Callback on completion
 */
export class DialogManager {
  private scene: Phaser.Scene;
  private container!: Phaser.GameObjects.Container;
  private background!: Phaser.GameObjects.Rectangle;
  private speakerText!: Phaser.GameObjects.Text;
  private dialogText!: Phaser.GameObjects.Text;
  private continueIndicator!: Phaser.GameObjects.Text;
  private portrait!: Phaser.GameObjects.Sprite;
  private choiceTexts: Phaser.GameObjects.Text[] = [];
  
  private textTyping: any; // TextTyping plugin instance
  private currentConfig: DialogConfig | null = null;
  private currentLineIndex = 0;
  private isTyping = false;
  private isWaitingForInput = false;
  private selectedChoiceIndex = 0;
  
  // Dimensions
  private boxWidth: number;
  private boxHeight: number;
  private boxX: number;
  private boxY: number;
  private padding = 16;
  
  private inputKeys: {
    action?: Phaser.Input.Keyboard.Key;
    up?: Phaser.Input.Keyboard.Key;
    down?: Phaser.Input.Keyboard.Key;
  } = {};

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    
    // Dialog box dimensions - bottom of screen
    this.boxWidth = scene.scale.width - 40;
    this.boxHeight = 120;
    this.boxX = 20;
    this.boxY = scene.scale.height - this.boxHeight - 20;
    
    this.createUI();
    this.setupInput();
  }

  private createUI(): void {
    // Container holds all dialog elements
    this.container = this.scene.add.container(0, 0);
    this.container.setDepth(1000); // Always on top
    this.container.setVisible(false);
    
    // Semi-transparent background
    this.background = this.scene.add.rectangle(
      this.boxX + this.boxWidth / 2,
      this.boxY + this.boxHeight / 2,
      this.boxWidth,
      this.boxHeight,
      0x000000,
      0.85
    );
    this.background.setStrokeStyle(2, 0xffffff);
    this.container.add(this.background);
    
    // Portrait area (left side, optional)
    this.portrait = this.scene.add.sprite(
      this.boxX + 50,
      this.boxY + this.boxHeight / 2,
      ''
    );
    this.portrait.setVisible(false);
    this.container.add(this.portrait);
    
    // Speaker name (top left)
    this.speakerText = this.scene.add.text(
      this.boxX + this.padding,
      this.boxY + this.padding - 4,
      '',
      {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '12px',
        color: '#ffcc00',
      }
    );
    this.container.add(this.speakerText);
    
    // Dialog text (main area)
    this.dialogText = this.scene.add.text(
      this.boxX + this.padding,
      this.boxY + this.padding + 20,
      '',
      {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '10px',
        color: '#ffffff',
        wordWrap: { width: this.boxWidth - this.padding * 2 - 20 },
        lineSpacing: 6,
      }
    );
    this.container.add(this.dialogText);
    
    // Continue indicator (bottom right, blinking)
    this.continueIndicator = this.scene.add.text(
      this.boxX + this.boxWidth - this.padding - 20,
      this.boxY + this.boxHeight - this.padding - 10,
      '▼',
      {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '10px',
        color: '#ffffff',
      }
    );
    this.continueIndicator.setVisible(false);
    this.container.add(this.continueIndicator);
    
    // Blinking animation for continue indicator
    this.scene.tweens.add({
      targets: this.continueIndicator,
      alpha: { from: 1, to: 0.3 },
      duration: 500,
      yoyo: true,
      repeat: -1,
    });
    
    // Initialize TextTyping on dialogText
    this.textTyping = this.scene.plugins.get('rexTextTyping');
    if (!this.textTyping) {
      console.warn('[DialogManager] rexTextTyping plugin not loaded, falling back to instant text');
    }
  }

  private setupInput(): void {
    if (!this.scene.input.keyboard) return;
    
    this.inputKeys.action = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.inputKeys.up = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
    this.inputKeys.down = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);
    
    // Also accept Enter and Z as action keys
    const enterKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
    const zKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.Z);
    
    enterKey.on('down', () => this.handleAction());
    zKey.on('down', () => this.handleAction());
    this.inputKeys.action.on('down', () => this.handleAction());
    this.inputKeys.up?.on('down', () => this.handleChoiceNavigation(-1));
    this.inputKeys.down?.on('down', () => this.handleChoiceNavigation(1));
  }

  /**
   * Start a dialog sequence
   */
  show(config: DialogConfig): void {
    this.currentConfig = config;
    this.currentLineIndex = 0;
    this.container.setVisible(true);
    this.showLine(0);
  }

  /**
   * Hide the dialog box
   */
  hide(): void {
    this.container.setVisible(false);
    this.currentConfig = null;
    this.clearChoices();
  }

  /**
   * Check if dialog is currently active
   */
  isActive(): boolean {
    return this.container.visible;
  }

  private showLine(index: number): void {
    if (!this.currentConfig || index >= this.currentConfig.lines.length) {
      // No more lines - show choices or complete
      if (this.currentConfig?.choices && this.currentConfig.choices.length > 0) {
        this.showChoices();
      } else {
        this.complete();
      }
      return;
    }
    
    const line = this.currentConfig.lines[index];
    this.speakerText.setText(line.speaker);
    this.continueIndicator.setVisible(false);
    this.isWaitingForInput = false;
    
    // Handle portrait
    if (line.portrait) {
      // Try to show portrait - assumes atlas is loaded
      this.portrait.setTexture(line.portrait);
      this.portrait.setVisible(true);
      this.portrait.setScale(0.5);
    } else {
      this.portrait.setVisible(false);
    }
    
    // Type out the text
    this.typeText(line.text);
  }

  private typeText(text: string): void {
    this.dialogText.setText('');
    this.isTyping = true;
    
    if (this.textTyping) {
      // Use rexrainbow TextTyping
      const typing = this.textTyping.add(this.dialogText, {
        speed: 30, // ms per character
        setTextCallback: (text: string) => text,
      });
      
      typing.start(text);
      typing.once('complete', () => {
        this.onTypingComplete();
      });
    } else {
      // Fallback: instant text
      this.dialogText.setText(text);
      this.onTypingComplete();
    }
  }

  private onTypingComplete(): void {
    this.isTyping = false;
    this.isWaitingForInput = true;
    this.continueIndicator.setVisible(true);
  }

  private handleAction(): void {
    if (!this.container.visible) return;
    
    if (this.isTyping) {
      // Skip typing animation - show full text immediately
      if (this.textTyping && this.currentConfig) {
        const line = this.currentConfig.lines[this.currentLineIndex];
        if (line) {
          this.dialogText.setText(line.text);
          this.onTypingComplete();
        }
      }
      return;
    }
    
    if (this.isWaitingForInput) {
      // Move to next line
      this.currentLineIndex++;
      this.showLine(this.currentLineIndex);
      return;
    }
    
    // If showing choices, select current choice
    if (this.choiceTexts.length > 0) {
      this.selectChoice(this.selectedChoiceIndex);
    }
  }

  private showChoices(): void {
    if (!this.currentConfig?.choices) return;
    
    this.clearChoices();
    this.continueIndicator.setVisible(false);
    this.speakerText.setText('');
    this.dialogText.setText('Choose:');
    
    const startY = this.boxY + this.padding + 35;
    
    this.currentConfig.choices.forEach((choice, index) => {
      const text = this.scene.add.text(
        this.boxX + this.padding + 20,
        startY + index * 22,
        choice.text,
        {
          fontFamily: '"Press Start 2P", monospace',
          fontSize: '10px',
          color: index === 0 ? '#ffcc00' : '#ffffff',
        }
      );
      text.setInteractive({ useHandCursor: true });
      text.on('pointerover', () => this.highlightChoice(index));
      text.on('pointerdown', () => this.selectChoice(index));
      this.choiceTexts.push(text);
      this.container.add(text);
    });
    
    this.selectedChoiceIndex = 0;
    this.updateChoiceHighlight();
  }

  private clearChoices(): void {
    this.choiceTexts.forEach(t => t.destroy());
    this.choiceTexts = [];
    this.selectedChoiceIndex = 0;
  }

  private handleChoiceNavigation(delta: number): void {
    if (this.choiceTexts.length === 0) return;
    
    this.selectedChoiceIndex = Phaser.Math.Wrap(
      this.selectedChoiceIndex + delta,
      0,
      this.choiceTexts.length
    );
    this.updateChoiceHighlight();
  }

  private highlightChoice(index: number): void {
    this.selectedChoiceIndex = index;
    this.updateChoiceHighlight();
  }

  private updateChoiceHighlight(): void {
    this.choiceTexts.forEach((text, i) => {
      text.setColor(i === this.selectedChoiceIndex ? '#ffcc00' : '#ffffff');
    });
  }

  private selectChoice(index: number): void {
    if (!this.currentConfig?.choices) return;
    
    const choice = this.currentConfig.choices[index];
    if (choice.action) {
      choice.action();
    }
    
    // For now, just complete after choice
    this.complete();
  }

  private complete(): void {
    const onComplete = this.currentConfig?.onComplete;
    this.hide();
    if (onComplete) {
      onComplete();
    }
  }

  /**
   * Convenience method to show a simple single-line dialog
   */
  say(speaker: string, text: string, onComplete?: () => void): void {
    this.show({
      lines: [{ speaker, text }],
      onComplete,
    });
  }

  /**
   * Show a random quote from an array
   */
  sayRandom(speaker: string, quotes: string[], onComplete?: () => void): void {
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    this.say(speaker, randomQuote, onComplete);
  }

  /**
   * Destroy the dialog manager and clean up
   */
  destroy(): void {
    this.container.destroy();
  }
}
